import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Custom hook for WebSocket connection to backend
 * @param {number} projectId - Project ID to listen for
 * @param {Function} onMessage - Callback for incoming messages
 * @returns {Object} - WebSocket connection state
 */
export function useWebSocket(projectId, onMessage) {
  const ws = useRef(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const reconnectTimeout = useRef(null);
  const onMessageRef = useRef(onMessage);

  // Keep onMessage ref up to date without triggering reconnects
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    // Determine WebSocket URL based on environment
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    let wsUrl;
    if (isDev) {
      // Dev: Connect directly to backend
      wsUrl = 'ws://localhost:3000/ws';
    } else {
      // Prod: Use same domain (proxied through Nginx)
      // Automatically uses wss:// if page is https://
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/ws`;
    }

    const connect = () => {
      try {
        console.log(`[WebSocket] Connecting to ${wsUrl}...`);

        ws.current = new WebSocket(wsUrl);

        ws.current.onopen = () => {
          console.log('[WebSocket] Connected');
          setConnected(true);
          setError(null);
        };

        ws.current.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);

            // Only process messages for this project
            if (message.projectId === projectId || !message.projectId) {
              console.log('[WebSocket] Message:', message);
              onMessageRef.current(message);
            }
          } catch (err) {
            console.error('[WebSocket] Failed to parse message:', err);
          }
        };

        ws.current.onerror = (err) => {
          console.error('[WebSocket] Error:', err);
          setError('WebSocket connection error');
        };

        ws.current.onclose = () => {
          console.log('[WebSocket] Disconnected');
          setConnected(false);

          // Attempt reconnection after 3 seconds
          reconnectTimeout.current = setTimeout(() => {
            console.log('[WebSocket] Attempting reconnection...');
            connect();
          }, 3000);
        };
      } catch (err) {
        console.error('[WebSocket] Connection error:', err);
        setError(err.message);
      }
    };

    // Connect on mount
    connect();

    // Cleanup on unmount
    return () => {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [projectId]); // Only reconnect when projectId changes

  return {
    connected,
    error
  };
}
